import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutoAwesome,
  Close,
  EmailOutlined,
  PersonOutline,
  RefreshOutlined,
  SendRounded,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { trackCustomEvent } from "../../common/tracking";

const TOKEN_KEY = "beyundai_lead_closer_token";
const CONTACT_TOKEN_KEY = "beyundai_lead_closer_contact_token";
const CONTACT_NAME_KEY = "beyundai_lead_closer_name";
const CONTACT_EMAIL_KEY = "beyundai_lead_closer_email";

const suggestions = [
  "Which plan is right for me?",
  "How many credits do I need?",
  "What can I create with Beyund AI?",
];

const contactGreeting =
  "Hi! Before we begin, please share your name and email address. Then I’ll help you choose the right plan.";

const readyGreeting =
  "Hi! Tell me what you want to create and I’ll help you choose the right plan.";

function buildGreeting(contactReady, contactName) {
  if (!contactReady) return contactGreeting;
  if (contactName) {
    return `Welcome back, ${contactName}. What would you like to know about Beyund AI?`;
  }
  return readyGreeting;
}

const MessageContent = ({ content }) => {
  const lines = String(content || "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\s*#{1,6}\s*/gm, "")
    .replace(/^\s*[*-]\s+/gm, "• ")
    .replace(/\*/g, "")
    .replace(/`{1,3}/g, "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <Stack spacing={0.8}>
      {lines.map((line, index) => (
        <Typography
          key={`${line}-${index}`}
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
        >
          {line}
        </Typography>
      ))}
    </Stack>
  );
};

const AiLeadCloserWidget = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [name, setName] = React.useState(
    () => localStorage.getItem(CONTACT_NAME_KEY) || "",
  );
  const [email, setEmail] = React.useState(
    () => localStorage.getItem(CONTACT_EMAIL_KEY) || "",
  );
  const [contactError, setContactError] = React.useState("");
  const [savingContact, setSavingContact] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [startingNew, setStartingNew] = React.useState(false);
  const [token, setToken] = React.useState(
    () => localStorage.getItem(TOKEN_KEY) || "",
  );
  const [contactReady, setContactReady] = React.useState(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY) || "";
    return Boolean(
      storedToken && localStorage.getItem(CONTACT_TOKEN_KEY) === storedToken,
    );
  });
  const [messages, setMessages] = React.useState(() => [
    {
      role: "assistant",
      content: buildGreeting(
        Boolean(
          localStorage.getItem(TOKEN_KEY) &&
            localStorage.getItem(CONTACT_TOKEN_KEY) ===
              localStorage.getItem(TOKEN_KEY),
        ),
        localStorage.getItem(CONTACT_NAME_KEY) || "",
      ),
    },
  ]);
  const [hasTranscript, setHasTranscript] = React.useState(false);
  const scrollRef = React.useRef(null);
  const historyLoadedFor = React.useRef("");

  const persistSession = (nextToken, contactName, contactEmail) => {
    localStorage.setItem(TOKEN_KEY, nextToken);
    localStorage.setItem(CONTACT_TOKEN_KEY, nextToken);
    if (contactName) localStorage.setItem(CONTACT_NAME_KEY, contactName);
    if (contactEmail) localStorage.setItem(CONTACT_EMAIL_KEY, contactEmail);
    setToken(nextToken);
    setContactReady(true);
    if (contactName) setName(contactName);
    if (contactEmail) setEmail(contactEmail);
  };

  const clearViewerSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CONTACT_TOKEN_KEY);
    setToken("");
    setContactReady(false);
    setHasTranscript(false);
    historyLoadedFor.current = "";
  };

  const hydrateFromHistory = React.useCallback(
    async (sessionToken) => {
      if (!sessionToken) return;
      if (historyLoadedFor.current === sessionToken) return;
      setLoadingHistory(true);
      try {
        const res = await hitAxios({
          path: "/api/lead-closer/history",
          post: true,
          admin: false,
          obj: { token: sessionToken },
          showLoading: false,
          showSnackbar: false,
        });
        if (!res?.data?.success) {
          if (
            res?.data?.code === "NOT_FOUND" ||
            /not found/i.test(res?.data?.msg || "")
          ) {
            clearViewerSession();
            setMessages([{ role: "assistant", content: contactGreeting }]);
          }
          return;
        }

        const data = res.data.data || {};
        historyLoadedFor.current = sessionToken;
        const ready = Boolean(data.contact_ready);
        setContactReady(ready);
        if (ready) {
          localStorage.setItem(CONTACT_TOKEN_KEY, sessionToken);
        }
        if (data.name) {
          setName(data.name);
          localStorage.setItem(CONTACT_NAME_KEY, data.name);
        }
        if (data.email) {
          setEmail(data.email);
          localStorage.setItem(CONTACT_EMAIL_KEY, data.email);
        }

        const history = Array.isArray(data.messages) ? data.messages : [];
        if (history.length) {
          setHasTranscript(true);
          setMessages(
            history.map((row) => ({
              role: row.role === "user" ? "user" : "assistant",
              content: row.content,
            })),
          );
        } else {
          setHasTranscript(false);
          setMessages([
            {
              role: "assistant",
              content: buildGreeting(ready, data.name || ""),
            },
          ]);
        }
      } finally {
        setLoadingHistory(false);
      }
    },
    [hitAxios],
  );

  React.useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("openLeadCloser", show);
    return () => window.removeEventListener("openLeadCloser", show);
  }, []);

  React.useEffect(() => {
    if (!open || !token) return;
    void hydrateFromHistory(token);
  }, [open, token, hydrateFromHistory]);

  React.useEffect(() => {
    if (open) scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, sending, loadingHistory]);

  const createSession = async (contactName, contactEmail) => {
    const res = await hitAxios({
      path: "/api/lead-closer/session",
      post: true,
      admin: false,
      obj: {
        source_url: window.location.href,
        name: contactName,
        email: contactEmail,
      },
      showLoading: false,
      showSnackbar: false,
    });
    const next = res?.data?.data?.token;
    if (res?.data?.success && next) {
      return next;
    }
    throw new Error(res?.data?.msg || "Chat is unavailable right now.");
  };

  const startConversation = async () => {
    const contactName = name.trim();
    const contactEmail = email.trim().toLowerCase();
    if (contactName.length < 2) {
      setContactError("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setContactError("Please enter a valid email address.");
      return;
    }

    setSavingContact(true);
    setContactError("");
    try {
      let sessionToken = token;
      if (sessionToken) {
        const contactRes = await hitAxios({
          path: "/api/lead-closer/contact",
          post: true,
          admin: false,
          obj: { token: sessionToken, name: contactName, email: contactEmail },
          showLoading: false,
          showSnackbar: false,
        });
        if (!contactRes?.data?.success) {
          clearViewerSession();
          sessionToken = "";
        }
      }
      if (!sessionToken) {
        sessionToken = await createSession(contactName, contactEmail);
      }

      persistSession(sessionToken, contactName, contactEmail);
      historyLoadedFor.current = sessionToken;
      setHasTranscript(false);
      setMessages([
        {
          role: "assistant",
          content: `Thanks, ${contactName}. What would you like to know about Beyund AI?`,
        },
      ]);
      trackCustomEvent("lead_closer_contact_shared");
    } catch (error) {
      setContactError(error?.message || "Chat is unavailable right now.");
    } finally {
      setSavingContact(false);
    }
  };

  const startNewChat = async () => {
    if (startingNew) return;
    const contactName =
      name.trim() || localStorage.getItem(CONTACT_NAME_KEY) || "";
    const contactEmail = (
      email.trim() ||
      localStorage.getItem(CONTACT_EMAIL_KEY) ||
      ""
    ).toLowerCase();

    if (
      contactName.length < 2 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
    ) {
      clearViewerSession();
      setMessages([{ role: "assistant", content: contactGreeting }]);
      return;
    }

    setStartingNew(true);
    try {
      const res = await hitAxios({
        path: "/api/lead-closer/new-chat",
        post: true,
        admin: false,
        obj: {
          token: token || undefined,
          source_url: window.location.href,
          name: contactName,
          email: contactEmail,
        },
        showLoading: false,
        showSnackbar: false,
      });
      const next = res?.data?.data?.token;
      if (!res?.data?.success || !next) {
        throw new Error(res?.data?.msg || "Could not start a new chat.");
      }

      persistSession(next, contactName, contactEmail);
      historyLoadedFor.current = next;
      setHasTranscript(false);
      setMessages([
        {
          role: "assistant",
          content: `Fresh start, ${contactName}. What would you like to create with Beyund AI?`,
        },
      ]);
      trackCustomEvent("lead_closer_new_chat");
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content:
            error?.message ||
            "I couldn’t start a new chat right now. Please try again.",
        },
      ]);
    } finally {
      setStartingNew(false);
    }
  };

  const send = async (preset) => {
    const text = String(preset || input).trim();
    if (!text || sending || !contactReady || !token) return;
    setInput("");
    setHasTranscript(true);
    setMessages((current) => [...current, { role: "user", content: text }]);
    setSending(true);
    trackCustomEvent("lead_closer_message_sent");
    try {
      const res = await hitAxios({
        path: "/api/lead-closer/message",
        post: true,
        admin: false,
        obj: { token, message: text },
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        if (res?.data?.code === "CONTACT_REQUIRED") {
          localStorage.removeItem(CONTACT_TOKEN_KEY);
          setContactReady(false);
        }
        if (/not found/i.test(res?.data?.msg || "")) {
          clearViewerSession();
        }
        throw new Error(res?.data?.msg || "I couldn’t answer that right now.");
      }
      setMessages((current) => [
        ...current,
        { role: "assistant", content: res.data.data.reply },
      ]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content:
            error?.message ||
            "I’m temporarily unavailable. Please try again shortly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const openWidget = () => {
    setOpen(true);
    trackCustomEvent("lead_closer_opened");
  };

  const showSuggestions =
    contactReady && !hasTranscript && messages.length <= 2 && !loadingHistory;

  return (
    <>
      {!open && (
        <Button
          variant="contained"
          onClick={openWidget}
          startIcon={<AutoAwesome />}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: 1300,
            borderRadius: 99,
            px: 2.5,
            py: 1.3,
            color: "#fff",
            background: "linear-gradient(120deg, #7c3aed, #db2777, #f97316)",
            boxShadow: "0 16px 45px rgba(219, 39, 119, 0.42)",
            animation: "assistantGlow 2.4s ease-in-out infinite",
            "@keyframes assistantGlow": {
              "0%, 100%": {
                transform: "translateY(0)",
                boxShadow: "0 16px 45px rgba(219, 39, 119, 0.38)",
              },
              "50%": {
                transform: "translateY(-3px)",
                boxShadow: "0 20px 55px rgba(124, 58, 237, 0.56)",
              },
            },
            "&:hover": {
              background: "linear-gradient(120deg, #6d28d9, #be185d, #ea580c)",
            },
          }}
        >
          Ask Beyund Assistant
        </Button>
      )}

      {open && (
        <Paper
          role="dialog"
          aria-label="Beyund Assistant"
          elevation={18}
          sx={{
            position: "fixed",
            zIndex: 1400,
            right: mobile ? 0 : 24,
            bottom: mobile ? 0 : 24,
            width: mobile ? "100%" : 390,
            height: mobile ? "100dvh" : 620,
            maxHeight: mobile ? "100dvh" : "calc(100vh - 48px)",
            borderRadius: mobile ? 0 : 3,
            overflow: "hidden",
            border: "2px solid transparent",
            backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(135deg, #7c3aed, #db2777, #f97316)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            boxShadow: "0 28px 80px rgba(76, 29, 149, 0.4)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              color: "#fff",
              background:
                "linear-gradient(120deg, #6d28d9 0%, #db2777 55%, #f97316 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900}>Beyund Assistant</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                I am here for you. Ask anything, I'll guide you
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.25} alignItems="center">
              {contactReady && (
                <Tooltip title="Start a new chat">
                  <span>
                    <IconButton
                      color="inherit"
                      onClick={startNewChat}
                      disabled={startingNew || sending}
                      aria-label="Start new chat"
                      size="small"
                    >
                      {startingNew ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <RefreshOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton
                color="inherit"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <Close />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              bgcolor: "background.default",
            }}
          >
            {loadingHistory ? (
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ py: 6 }}
              >
                <CircularProgress size={22} />
                <Typography variant="caption" color="text.secondary">
                  Restoring your conversation…
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.2}>
                {messages.map((message, index) => (
                  <Box
                    key={`${message.role}-${index}-${String(message.content).slice(0, 24)}`}
                    sx={{
                      alignSelf:
                        message.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "88%",
                      px: 1.5,
                      py: 1.1,
                      borderRadius: 2,
                      bgcolor:
                        message.role === "user"
                          ? "primary.main"
                          : "background.paper",
                      color:
                        message.role === "user"
                          ? "primary.contrastText"
                          : "text.primary",
                      border:
                        message.role === "assistant"
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                      background:
                        message.role === "assistant"
                          ? `linear-gradient(135deg, ${alpha("#7c3aed", 0.1)}, ${alpha("#db2777", 0.07)})`
                          : "linear-gradient(120deg, #7c3aed, #db2777)",
                    }}
                  >
                    <MessageContent content={message.content} />
                  </Box>
                ))}
                {sending && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Finding the best answer…
                    </Typography>
                  </Stack>
                )}
                <div ref={scrollRef} />
              </Stack>
            )}
          </Box>

          {showSuggestions && (
            <Stack
              direction="row"
              spacing={0.7}
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 1.5, py: 1, maxHeight: 92, overflowY: "auto" }}
            >
              {suggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  onClick={() => send(suggestion)}
                  sx={{ borderRadius: 99, fontSize: "0.7rem", lineHeight: 1.2 }}
                >
                  {suggestion}
                </Button>
              ))}
            </Stack>
          )}

          {!contactReady ? (
            <Stack
              component="form"
              spacing={1.2}
              onSubmit={(event) => {
                event.preventDefault();
                startConversation();
              }}
              sx={{
                p: 1.5,
                bgcolor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <TextField
                required
                fullWidth
                size="small"
                label="Your name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                inputProps={{ maxLength: 120 }}
                InputProps={{
                  startAdornment: <PersonOutline sx={{ mr: 1, fontSize: 18 }} />,
                }}
              />
              <TextField
                required
                fullWidth
                size="small"
                type="email"
                label="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  startAdornment: <EmailOutlined sx={{ mr: 1, fontSize: 18 }} />,
                }}
              />
              {contactError && (
                <Typography variant="caption" color="error">
                  {contactError}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.45 }}
              >
                We’ll keep your information private and will not share your data
                with any third party.
              </Typography>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  savingContact || name.trim().length < 2 || !email.trim()
                }
                sx={{
                  color: "#fff",
                  background: "linear-gradient(120deg, #7c3aed, #db2777, #f97316)",
                }}
              >
                {savingContact ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Start chat"
                )}
              </Button>
            </Stack>
          ) : (
            <Stack
              component="form"
              direction="row"
              spacing={1}
              onSubmit={(event) => {
                event.preventDefault();
                send();
              }}
              sx={{
                p: 1.5,
                bgcolor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about plans or features…"
                inputProps={{ maxLength: 1000 }}
              />
              <IconButton
                type="submit"
                color="primary"
                disabled={!input.trim() || sending || loadingHistory}
                aria-label="Send message"
              >
                <SendRounded />
              </IconButton>
            </Stack>
          )}
        </Paper>
      )}
    </>
  );
};

export default AiLeadCloserWidget;
